import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import useHttp from "../hooks/useHttp";
import useChat from "../hooks/useChat";
import { S3File, useChatFiles } from "../hooks/useChatFiles";
import { getEnvironmentApiUrl } from "../services/General";
import { IMessage } from "../interfaces/Chat";
import GeneralContext from "./GeneralContext";
import { getUser } from "../services/Auth";
import { usePatientChats } from "../hooks/usePatientChats";

export interface ChimeCredentials {
  region: string; // chime region for text chat
  app_instance_user_arn: string;
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  expiration: string;
  attachments_s3_bucket: string;
  attachments_s3_bucket_region: string; // s3 region for files chat
}

interface Membership {
  id: number;
  patient_chat_id: number;
  chimesdk_app_instance_user_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface PatientChat {
  id: number;
  patient_id: number;
  patient_has_unread_msg: boolean;
  user_has_unread_msg: boolean;
  chimesdk_channel_arn: string;
  attachments_s3_key?: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  has_activity: boolean;
  memberships: Membership[];
}

export interface PatientChatsMap {
  [patientId: number]: PatientChat;
}

type MessageMetadata = Record<string, unknown>;
type MessagesByPatient = Record<number, IMessage[]>;

interface ChatContextValue {
  messages: IMessage[];
  sendMessage: (content: string, customMetadata?: MessageMetadata) => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  channelArn: string | null;
  setPatientId: React.Dispatch<React.SetStateAction<number | null>>;
  hasUnreadMessages?: boolean;
  listFiles?: () => Promise<S3File[]>;
  downloadFile?: (fileKey: string) => Promise<Blob | null>;
  failedMessages: IMessage[];
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<Props> = ({ children }) => {
  const { isChatOpen } = useContext(GeneralContext);

  const user = getUser();

  const { sendRequest: getChimeCredentials } = useHttp<ChimeCredentials>();
  const { sendRequest: markChatRead } = useHttp<unknown>();
  const { sendRequest: initiateOrJoinChat } = useHttp<PatientChatsMap>();
  const { sendRequest: notifyMessageSent } = useHttp<any>();

  const [patientId, setPatientId] = useState<number | null>(null);
  const [failedMessagesByPatient, setFailedMessagesByPatient] = useState<MessagesByPatient>({});
  const [pendingMessagesByPatient, setPendingMessagesByPatient] = useState<MessagesByPatient>({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const patientChats = usePatientChats(patientId ? [patientId] : [], true);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const patientChatId = useMemo(() => {
    if (!patientChats || !patientId) return null;
    return patientChats[patientId]?.id || null;
  }, [patientChats, patientId]);

  const currentUserName = useMemo(() => {
    if (!user) return "You";
    return `${user.first_name} ${user.last_name_1}`.trim();
  }, [user]);

  const fetchChimeCredentials = useCallback((): Promise<ChimeCredentials> => {
    return new Promise((resolve, reject) => {
      getChimeCredentials(
        { url: getEnvironmentApiUrl() + `patient_chat/chimesdk_credentials`, method: "GET" },
        (data) => resolve(data),
        (err) => reject(err)
      );
    });
  }, [getChimeCredentials]);

  useEffect(() => {
    if (!isChatOpen || !patientChatId) return;

    markChatRead(
      {
        url: getEnvironmentApiUrl() + `patient_chat/${patientChatId}/mark_chat_read`,
        method: "PUT",
      },
      () => {
        console.log("Chat marked as read:", patientChatId);
      },
      (err) => console.error(err)
    );
  }, [isChatOpen, patientChatId, markChatRead]);

  useEffect(() => {
    if (!isChatOpen || !patientId) return;

    initiateOrJoinChat(
      {
        url: getEnvironmentApiUrl() + `patient_chat/initiate_or_join`,
        method: "POST",
        body: {
          patient_id: patientId,
        },
      },
      (data) => {
        console.log("Initiate or join chat response:", data);
      },
      (err) => console.error(err)
    );
  }, [isChatOpen, initiateOrJoinChat, patientId]);

  const channelArn = useMemo(() => {
    if (!isChatOpen || !patientChats || !patientId) return null;
    return patientChats[patientId]?.chimesdk_channel_arn || "";
  }, [isChatOpen, patientChats, patientId]);

  const chatUser = useMemo(
    () => ({ uuid: user?.uuid, name: user?.firstName }),
    [user?.uuid, user?.firstName]
  );

  const chat = useChat(isChatOpen ? channelArn : null, fetchChimeCredentials, chatUser);

  const pendingMessages = useMemo(() => {
    if (!patientId) return [];
    return pendingMessagesByPatient[patientId] || [];
  }, [pendingMessagesByPatient, patientId]);

  const failedMessages = useMemo(() => {
    if (!patientId) return [];
    return failedMessagesByPatient[patientId] || [];
  }, [failedMessagesByPatient, patientId]);

  const messages = useMemo(() => {
    if (!pendingMessages.length) return chat.messages;

    const chatLocalIds = new Set(
      chat.messages
        .map((msg) => msg.localId)
        .filter((localId): localId is string => Boolean(localId))
    );

    const chatIds = new Set(
      chat.messages
        .map((msg) => msg.id)
        .filter((id): id is string => Boolean(id))
    );

    const visiblePending = pendingMessages.filter((msg) => {
      const messageId = msg.id;
      const messageLocalId = msg.localId;

      if (messageId && chatIds.has(messageId)) return false;
      if (messageLocalId && chatLocalIds.has(messageLocalId)) return false;

      return true;
    });

    return [...chat.messages, ...visiblePending];
  }, [chat.messages, pendingMessages]);

  const generateLocalId = useCallback(() => {
    return `local_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }, []);

  const getMessageKey = useCallback((message: IMessage) => {
    return message.id ?? message.localId ?? "";
  }, []);

  const addPendingMessage = useCallback((targetPatientId: number, message: IMessage) => {
    setPendingMessagesByPatient((prev) => ({
      ...prev,
      [targetPatientId]: [...(prev[targetPatientId] || []), message],
    }));
  }, []);

  const updatePendingMessage = useCallback(
    (targetPatientId: number, messageId: string, updater: (msg: IMessage) => IMessage) => {
      setPendingMessagesByPatient((prev) => ({
        ...prev,
        [targetPatientId]: (prev[targetPatientId] || []).map((msg) => {
          const key = msg.id ?? msg.localId ?? "";
          return key === messageId ? updater(msg) : msg;
        }),
      }));
    },
    []
  );

  const removePendingMessage = useCallback((targetPatientId: number, messageId: string) => {
    setPendingMessagesByPatient((prev) => ({
      ...prev,
      [targetPatientId]: (prev[targetPatientId] || []).filter((msg) => {
        const key = msg.id ?? msg.localId ?? "";
        return key !== messageId;
      }),
    }));
  }, []);

  const addFailedMessage = useCallback((targetPatientId: number, message: IMessage) => {
    setFailedMessagesByPatient((prev) => ({
      ...prev,
      [targetPatientId]: [...(prev[targetPatientId] || []), message],
    }));
  }, []);

  const removeFailedMessage = useCallback((targetPatientId: number, messageId: string) => {
    setFailedMessagesByPatient((prev) => ({
      ...prev,
      [targetPatientId]: (prev[targetPatientId] || []).filter((msg) => {
        const key = msg.id ?? msg.localId ?? "";
        return key !== messageId;
      }),
    }));
  }, []);

  const retryMessage = useCallback(
    async (failedMsg: IMessage) => {
      if (!patientId) return;

      const messageKey = getMessageKey(failedMsg);
      if (!messageKey) return;

      try {
        removeFailedMessage(patientId, messageKey);

        updatePendingMessage(patientId, messageKey, (msg) => ({
          ...msg,
          status: "sending" as const,
        }));

        const updatedMetadata = { ...failedMsg.customMetadata, localId: failedMsg.localId };

        await chat.createMessage(failedMsg.content, updatedMetadata);

        updatePendingMessage(patientId, messageKey, (msg) => ({
          ...msg,
          status: "sent" as const,
        }));

        window.setTimeout(() => {
          removePendingMessage(patientId, messageKey);
        }, 3000);
      } catch (error) {
        console.error("Retry failed:", error);

        updatePendingMessage(patientId, messageKey, (msg) => ({
          ...msg,
          status: "failed" as const,
        }));

        addFailedMessage(patientId, { ...failedMsg, status: "failed" as const });
      }
    },
    [patientId, getMessageKey, removeFailedMessage, updatePendingMessage, chat, removePendingMessage, addFailedMessage]
  );

  useEffect(() => {
    if (isOnline && failedMessages.length > 0) {
      failedMessages.forEach((msg) => retryMessage(msg));
    }
  }, [isOnline, failedMessages, retryMessage]);

  const sendMessage = useCallback(
    async (content: string, customMetadata?: MessageMetadata) => {
      if (!channelArn || !patientId) return;

      const localId = generateLocalId();
      
      const updatedMetadata = { ...customMetadata, localId };

      const newMessage: IMessage = {
        id: localId,
        sender: currentUserName,
        content,
        timestamp: new Date().toISOString(),
        dataURL: "",
        dataType: "TEXT",
        customMetadata: updatedMetadata,
        senderType: "user",
        status: isOnline ? "sending" : "failed",
        localId,
      };

      addPendingMessage(patientId, newMessage);

      if (!isOnline) {
        addFailedMessage(patientId, newMessage);
        return;
      }

      try {
        await chat.createMessage(content, updatedMetadata);

        if (patientChatId) {
          notifyMessageSent(
            {
              url: getEnvironmentApiUrl() + `patient_chat/${patientChatId}/on_message_sent`,
              method: "POST",
            },
            () => undefined,
            () => undefined
          );
        }

        updatePendingMessage(patientId, localId, (msg) => ({
          ...msg,
          status: "sent" as const,
        }));

        window.setTimeout(() => {
          removePendingMessage(patientId, localId);
        }, 3000);
      } catch {
        updatePendingMessage(patientId, localId, (msg) => ({
          ...msg,
          status: "failed" as const,
        }));

        addFailedMessage(patientId, { ...newMessage, status: "failed" as const });
      }
    },
    [
      channelArn,
      patientId,
      generateLocalId,
      currentUserName,
      isOnline,
      addPendingMessage,
      addFailedMessage,
      chat,
      updatePendingMessage,
      removePendingMessage,
      patientChatId,
      notifyMessageSent
    ]
  );

  const { uploadFile, listFiles, downloadFile } = useChatFiles({
    patientId,
    patientChats,
    getCredentials: fetchChimeCredentials,
    sendMessage: chat.createMessage,
    channelArn,
  });

  const hasUnreadMessages =
    !!(patientChats && patientId && patientChats[patientId]?.user_has_unread_msg);

  const value: ChatContextValue = {
    messages,
    sendMessage,
    uploadFile,
    channelArn,
    setPatientId,
    hasUnreadMessages,
    listFiles,
    downloadFile,
    failedMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (!context) throw new Error("useChatContext must be used within a ChatProvider");
  return context;
};